import * as localforage from "localforage";
import {
    DASHBOARD_SUMMARY_CACHE_KEY,
    LOCATION_ECO_DATA_CACHE_KEY,
    LOCATION_REGION_DATA_CACHE_KEY
} from "./birdDataCache";

type DiagnosticsSnapshot = {
    rawObservationCount: number,
    observationsByLocationCount: number,
    observationsBySpeciesCount: number,
    checklistFamilyCount: number
}

type DiagnosticsKeySummary = {
    key: string,
    bytes: number,
    formattedSize: string,
    recordCount?: number
}

type MemoryDiagnostics = {
    jsHeapSizeLimit?: number,
    totalJSHeapSize?: number,
    usedJSHeapSize?: number
}

const STORED_EBIRD_DATA_KEY = 'storedEbirdData';

function formatBytes(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function estimateSerializedBytes(value: unknown) {
    try {
        return new Blob([JSON.stringify(value)]).size;
    } catch (error) {
        return 0;
    }
}

function getRecordCount(key: string, value: any) {
    if (!value || typeof value !== 'object') return undefined;

    if (key === STORED_EBIRD_DATA_KEY && Array.isArray(value.data)) {
        return value.data.length;
    }

    if (key === LOCATION_ECO_DATA_CACHE_KEY && value.ecoDataByLocationId && typeof value.ecoDataByLocationId === 'object') {
        return Object.keys(value.ecoDataByLocationId).length;
    }

    if (key === LOCATION_REGION_DATA_CACHE_KEY && value.regionDataByLocationId && typeof value.regionDataByLocationId === 'object') {
        return Object.keys(value.regionDataByLocationId).length;
    }

    if (key === DASHBOARD_SUMMARY_CACHE_KEY && value.recentActivity && Array.isArray(value.recentActivity)) {
        return value.recentActivity.length;
    }

    return undefined;
}

async function getKeySummaries(): Promise<DiagnosticsKeySummary[]> {
    const keys = await localforage.keys();
    const summaries: DiagnosticsKeySummary[] = [];

    for (const key of keys) {
        const value = await localforage.getItem(key);
        const bytes = estimateSerializedBytes(value);
        summaries.push({
            key,
            bytes,
            formattedSize: formatBytes(bytes),
            recordCount: getRecordCount(key, value)
        });
    }

    return summaries.sort((left, right) => right.bytes - left.bytes);
}

function getMemoryDiagnostics(): MemoryDiagnostics | undefined {
    const memory = (performance as Performance & {memory?: MemoryDiagnostics}).memory;
    if (!memory) return undefined;

    return {
        jsHeapSizeLimit: memory.jsHeapSizeLimit,
        totalJSHeapSize: memory.totalJSHeapSize,
        usedJSHeapSize: memory.usedJSHeapSize
    };
}

export async function runBirdMapDiagnostics(snapshot: DiagnosticsSnapshot) {
    const keySummaries = await getKeySummaries();
    const totalStoredBytes = keySummaries.reduce((sum, item) => sum + item.bytes, 0);
    const memory = getMemoryDiagnostics();

    const report = {
        snapshot,
        cache: {
            totalStoredBytes,
            totalStoredFormatted: formatBytes(totalStoredBytes),
            keySummaries
        },
        memory
    };

    console.groupCollapsed('BirdMap diagnostics');
    console.log('Snapshot', snapshot);
    console.log('Cache total', {
        totalStoredBytes,
        totalStoredFormatted: formatBytes(totalStoredBytes)
    });
    console.table(keySummaries.map(item => ({
        key: item.key,
        size: item.formattedSize,
        bytes: item.bytes,
        recordCount: item.recordCount ?? ''
    })));
    if (memory) {
        console.log('Memory', {
            jsHeapSizeLimit: memory.jsHeapSizeLimit ? formatBytes(memory.jsHeapSizeLimit) : undefined,
            totalJSHeapSize: memory.totalJSHeapSize ? formatBytes(memory.totalJSHeapSize) : undefined,
            usedJSHeapSize: memory.usedJSHeapSize ? formatBytes(memory.usedJSHeapSize) : undefined,
            raw: memory
        });
    }
    console.groupEnd();

    return report;
}

export function registerBirdMapDiagnostics(snapshot: DiagnosticsSnapshot) {
    window.birdMapDiagnostics = {
        run: () => runBirdMapDiagnostics(snapshot)
    };
}

